import { Link } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { Suspense, lazy, type ComponentType } from 'react'
import { z } from 'zod'

import { isStaleChunkError, reloadForStaleChunk } from '@/lib/staleChunk'

/**
 * `/skel` — Bones home (docs/SKEL-EDITOR.md §5.4 "Routes"): skeleton projects, recent parts sheets,
 * and the three ways to start one. The page lives in `features/skel/SkelHome` and is loaded lazily,
 * so the skeleton studio's code stays out of the main bundle.
 *
 * `lazySkelPage` is shared by the three `/skel*` routes: `React.lazy` with a resolved fallback, so
 * a chunk that fails to load (a redeploy while the tab was open) explains itself instead of
 * tearing the route down — the `lazySibling` pattern of `annotate.$kind.$uid.tsx`.
 */

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)

/**
 * No search params yet: every key is dropped. (Typed `{}` on purpose — a zod `z.object({})` infers
 * `Record<string, never>`, which poisons TanStack's `to: '.'` search typing for every other route.)
 */
export type SkelIndexSearch = Record<never, never>

export function validateSkelIndexSearch(_input: Record<string, unknown>): SkelIndexSearch {
  return {}
}

export const SKEL_INDEX_ROUTE_ID = '/skel' as const

/** Crockford base32 ULID, as the server mints ids (`app.ids.ulid`). */
export const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/

export { lenient as lenientSkel }

export function lazySkelPage<P extends object>(load: () => Promise<{ default: ComponentType<P> }>, what: string): ComponentType<P> {
  return lazy(async () => {
    try {
      return await load()
    } catch (err) {
      console.error(`[skel] could not load the ${what} page`, err)
      // A tab opened before a redeploy asks for chunks that no longer exist: reload once for the
      // new build instead of showing a dead end.
      if (isStaleChunkError(err) && reloadForStaleChunk()) {
        return { default: () => <SkelPageLoading label="Updating the studio…" /> }
      }
      return {
        default: function MissingSkelPage() {
          return (
            <div className="mx-auto flex w-full max-w-2xl flex-col items-start gap-2 px-4 py-12" role="alert">
              <h1 className="text-lg font-semibold text-ink">The {what} could not be opened</h1>
              <p className="text-sm text-ink-2">Your work is saved on the server. Reloading the page usually fixes this.</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm font-medium text-accent underline-offset-4 hover:underline focus-visible:focus-ring"
              >
                Reload the page
              </button>
              <Link to="/skel" className="text-sm font-medium text-accent underline-offset-4 hover:underline focus-visible:focus-ring">
                Back to Bones
              </Link>
            </div>
          )
        },
      }
    }
  })
}

export function SkelPageLoading({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 p-8 text-sm text-ink-2" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </p>
  )
}

const SkelHome = lazySkelPage(() => import('@/features/skel/SkelHome'), 'Bones page')

export function SkelIndexPage() {
  return (
    <Suspense fallback={<SkelPageLoading label="Opening Bones…" />}>
      <SkelHome />
    </Suspense>
  )
}
